import type { Agent } from '@studio/models/agent';
import type { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useCallback, useMemo } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import type { StoreApi } from 'zustand/vanilla';
import {
  addCentralDispatchSubscriber,
  addDiscoveryDispatchSubscriber,
  clearCentralDispatchSubscriber
} from './central-dispatch.js';
import { multiDeviceStateQuery, multiProxyStateQuery, studioStateQuery } from './queries.js';
import type { MultiDeviceSelector, StudioSelector } from './types.js';
import type { Proxy } from '#~/agent/types.js';

type SnapshotKey = string | symbol;
type QuerySelection<T> = {
  readonly key: SnapshotKey;
  readonly value: T;
};
type StateQueryStore = Pick<StoreApi<number>, 'getInitialState' | 'getState' | 'subscribe'>;

const haveSameSnapshotKey = <T>(previous: QuerySelection<T>, next: QuerySelection<T>): boolean =>
  Object.is(previous.key, next.key);

const jsonSnapshotKey = <T>(snapshot: T): SnapshotKey => {
  try {
    return `json:${JSON.stringify(snapshot) ?? 'undefined'}`;
  } catch (error) {
    if (error instanceof TypeError) {
      // Non-serializable snapshots must never be mistaken for unchanged state.
      return Symbol('non-serializable state query snapshot');
    }

    throw error;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const discoveryValue = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const nestedValue = value['value'];
  return isRecord(nestedValue) ? nestedValue : undefined;
};

const normalizedAgentIds = (value: unknown): string => {
  if (!Array.isArray(value)) {
    return JSON.stringify(value) ?? 'undefined';
  }

  // Copy before sorting so diffing never mutates broker-owned discovery snapshots.
  return JSON.stringify([...value].sort());
};

const discoverySnapshotKey = <T>(snapshot: T): SnapshotKey => {
  if (!isRecord(snapshot)) {
    return jsonSnapshotKey(snapshot);
  }

  try {
    const entries = Object.keys(snapshot)
      .sort()
      .map((key) => {
        const value = snapshot[key];
        const discovery = discoveryValue(value);

        return discovery === undefined
          ? [key, 'value', value]
          : [
              key,
              'discovery',
              discovery['id'],
              discovery['name'],
              discovery['agentId'],
              normalizedAgentIds(discovery['agentIds']),
              discovery['irisEnabled'],
              // Extended Discovery (ported from PR #1021's smartDiscoveryDiff): the
              // encoder stamps modelId/brandId/brand onto an ALREADY-discovered
              // device AFTER the initial emit (async fingerprint match), keeping the
              // same device key. Without these in the snapshot key, that
              // enrichment-only delta is swallowed and the row stays
              // "unfingerprinted" until a full wizard remount — the reason "Search
              // again" needed many clicks while close+reopen showed everything.
              // Include them so the 2s poll repaints the row the moment the
              // fingerprint lands.
              discovery['modelId'],
              discovery['brandId'],
              discovery['brand']
            ];
      });

    return `discovery:${JSON.stringify(entries)}`;
  } catch (error) {
    if (error instanceof TypeError) {
      return Symbol('non-serializable discovery snapshot');
    }

    throw error;
  }
};

const createStateQueryStore = (connect: (refresh: () => void) => () => void): StateQueryStore => {
  // Per rule: think-in-systems (ai.knowledge/global) — keep dispatch stable across concurrent renders.
  let revision = 0;
  const getState = (): number => revision;

  return {
    getInitialState: getState,
    getState,
    subscribe: (notify): (() => void) => {
      const refresh = (): void => {
        const previousRevision = revision;
        revision += 1;
        notify(revision, previousRevision);
      };
      const disconnect = connect(refresh);

      // Revalidate after connecting so a dispatch between render and subscription cannot be lost.
      refresh();
      return disconnect;
    }
  };
};

const useStateQueryStore = <T>(
  query: () => T,
  connect: (refresh: () => void) => () => void,
  getSnapshotKey: (snapshot: T) => SnapshotKey
): T => {
  const store = useMemo(() => createStateQueryStore(connect), [connect]);
  const selectQuery = useCallback((): QuerySelection<T> => {
    const value = query();
    // Compute the comparison key with the query so each snapshot is serialized at most once.
    return { key: getSnapshotKey(value), value };
  }, [getSnapshotKey, query]);
  const selection = useStoreWithEqualityFn(store, selectQuery, haveSameSnapshotKey);

  return selection.value;
};

const subscribeToCentralDispatch = (refresh: () => void, minInterval = 1000): (() => void) => {
  const refreshLoop = addCentralDispatchSubscriber(refresh, minInterval);
  return () => {
    clearCentralDispatchSubscriber(refreshLoop);
  };
};

const subscribeToDiscoveryDispatch = (refresh: () => void, debounceMs: number): (() => void) => {
  const refreshLoop = addDiscoveryDispatchSubscriber(refresh, debounceMs, 1000);
  return () => {
    clearCentralDispatchSubscriber(refreshLoop);
  };
};

export const useMultiProxyStateQuery = <U>(
  selector: (state: T_Maybe<State>, id: Proxy['id']) => Record<Agent['id'], U>
) => {
  const query = useCallback(() => multiProxyStateQuery(selector), [selector]);
  const connect = useCallback((refresh: () => void) => subscribeToCentralDispatch(refresh), []);

  return useStateQueryStore(query, connect, jsonSnapshotKey);
};

/**
 * Specialized hook for discovery queries with smart diffing and debouncing
 * Reduces flickering of discovery sources in multi-bridge scenarios
 */
export const useMultiProxyDiscoveryStateQuery = <U>(
  selector: (state: T_Maybe<State>, id: Proxy['id']) => Record<Agent['id'], U>,
  debounceMs = 2000
) => {
  const query = useCallback(() => multiProxyStateQuery(selector), [selector]);
  const connect = useCallback(
    (refresh: () => void) => subscribeToDiscoveryDispatch(refresh, debounceMs),
    [debounceMs]
  );

  return useStateQueryStore(query, connect, discoverySnapshotKey);
};

export const useMultiDeviceStateQuery = <U>(selector: MultiDeviceSelector<U>) => {
  const { transform, minInterval } = selector;
  const effectiveMinInterval = minInterval ?? 1000;
  const query = useCallback(() => multiDeviceStateQuery(transform), [transform]);
  const connect = useCallback(
    (refresh: () => void) => subscribeToCentralDispatch(refresh, effectiveMinInterval),
    [effectiveMinInterval]
  );

  return useStateQueryStore(query, connect, jsonSnapshotKey);
};

export const useStudioStateQuery = <U>(selector: StudioSelector<U>) => {
  const { transform, minInterval } = selector;
  const effectiveMinInterval = minInterval ?? 1000;
  const query = useCallback(() => studioStateQuery(transform), [transform]);
  const connect = useCallback(
    (refresh: () => void) => subscribeToCentralDispatch(refresh, effectiveMinInterval),
    [effectiveMinInterval]
  );

  return useStateQueryStore(query, connect, jsonSnapshotKey);
};
